import { useNavigate, useParams } from "@tanstack/react-router"
import { useCallback } from "react"

import { GoodReceiptActionType, type GoodsReceiptActionState } from "../../../types"
import type { IncomingPurchaseOrdersFormValues } from "../schemas"
import { canSoftReceive } from "../utils"

import type { GoodsReceipt, GoodsReceiptProductDetail } from "@/graphql/codegen/graphql"
import { BasicPathType, useAppView } from "@/hooks/useAppView"
import { NotificationType } from "@/schemas/notification"
import { useGoodsReceiptActions } from "@/screens/WarehouseReceiving/hooks/useGoodsReceiptActions"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import type { ActionOutcome } from "@/types/common"

interface UseIncomingGoodsReceiptActionsProps {
  goodsReceipt: GoodsReceipt | null
  selectedPurchaseOrders: { orderId: number }[]
  formValues: IncomingPurchaseOrdersFormValues
  hasUnsavedChanges: boolean
  onRefetch: () => void
  onSave: () => Promise<ActionOutcome<GoodsReceiptProductDetail[]>>
  addPurchaseOrderSuccessMessage?: string
}

interface UseIncomingGoodsReceiptActionsReturn {
  actionState: GoodsReceiptActionState
  purchaseOrderToRemove: number | null
  handleDialogClose: () => void
  handleFormDialogSubmit: () => void
  handleEditGoodsReceipt: (goodsReceipt: GoodsReceipt) => void
  handleDeleteGoodsReceipt: (goodsReceipt: GoodsReceipt) => void
  handleGoodsReceiptDeleteSuccess: () => void
  handleAddPurchaseOrder: () => void
  handleConfirmAddPurchaseOrder: () => Promise<void>
  handleRemovePurchaseOrder: (purchaseOrderId: number) => void
  handlePurchaseOrderRemoveClose: () => void
  handlePurchaseOrderRemoveSuccess: () => void
  handleSoftReceive: () => Promise<void>
  handleSoftReceiveSuccess: () => void
  handleSoftReceiveSuccessSubmit: () => void
  handleUploadFiles: () => void
}

export const useIncomingGoodsReceiptActions = ({
  goodsReceipt,
  selectedPurchaseOrders,
  formValues,
  hasUnsavedChanges,
  onRefetch,
  onSave,
  addPurchaseOrderSuccessMessage,
}: UseIncomingGoodsReceiptActionsProps): UseIncomingGoodsReceiptActionsReturn => {
  const navigate = useNavigate()
  const { isTabletView, getBasicPathFor } = useAppView()
  const { goodsReceiptId } = useParams({
    from: `${getBasicPathFor(BasicPathType.WAREHOUSE_RECEIVING)}/incoming/$goodsReceiptId/_layout`,
  })
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const {
    actionState,
    purchaseOrderToRemove,
    handleDialogClose,
    handleEditGoodsReceipt,
    handleAddPurchaseOrder,
    handleConfirmAddPurchaseOrder,
    handleRemovePurchaseOrder,
    handlePurchaseOrderRemoveClose,
    handlePurchaseOrderRemoveSuccess,
    handleFormDialogSubmit,
    handleUploadFiles,
    setActionState,
  } = useGoodsReceiptActions({
    goodsReceipt,
    selectedPurchaseOrders,
    onRefetch,
    addPurchaseOrderSuccessMessage,
  })

  const handleDeleteGoodsReceipt = useCallback(
    (goodsReceipt: GoodsReceipt) => {
      setActionState({ type: GoodReceiptActionType.Delete, goodsReceipt })
    },
    [setActionState]
  )

  const handleGoodsReceiptDeleteSuccess = useCallback(() => {
    navigate({ to: `${getBasicPathFor(BasicPathType.WAREHOUSE_RECEIVING)}/incoming` })
  }, [navigate, getBasicPathFor])

  const handleSoftReceive = useCallback(async () => {
    if (!canSoftReceive(formValues)) {
      notify({
        type: NotificationType.Error,
        message: "Please fill in all fields to continue",
      })
      return
    }

    if (hasUnsavedChanges) {
      const result = await onSave()

      if (result.error) {
        return
      }
    }

    setActionState({ type: GoodReceiptActionType.SoftReceive })
  }, [formValues, hasUnsavedChanges, notify, setActionState, onSave])

  const handleSoftReceiveSuccess = useCallback(() => {
    if (isTabletView) {
      setActionState({ type: GoodReceiptActionType.SoftReceiveSuccess })
    } else {
      handleDialogClose()
      navigate({
        to: `${getBasicPathFor(BasicPathType.WAREHOUSE_RECEIVING)}/soft-received/$goodsReceiptId`,
        params: { goodsReceiptId },
      })
    }
  }, [handleDialogClose, isTabletView, navigate, getBasicPathFor, goodsReceiptId, setActionState])

  const handleSoftReceiveSuccessSubmit = useCallback(() => {
    navigate({
      to: `${getBasicPathFor(BasicPathType.WAREHOUSE_RECEIVING)}/soft-received/$goodsReceiptId`,
      params: { goodsReceiptId },
    })
  }, [navigate, getBasicPathFor, goodsReceiptId])

  return {
    actionState,
    purchaseOrderToRemove,
    handleDialogClose,
    handleFormDialogSubmit,
    handleEditGoodsReceipt,
    handleDeleteGoodsReceipt,
    handleGoodsReceiptDeleteSuccess,
    handleAddPurchaseOrder,
    handleRemovePurchaseOrder,
    handlePurchaseOrderRemoveClose,
    handlePurchaseOrderRemoveSuccess,
    handleConfirmAddPurchaseOrder,
    handleSoftReceive,
    handleSoftReceiveSuccess,
    handleUploadFiles,
    handleSoftReceiveSuccessSubmit,
  }
}
